import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NotificationService } from '@core/services/notifications.service';
import { environment } from '@env/environment';
import { FileType } from '@models/exports.model';
import { Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { MIMETYPES } from 'src/app/modules/exports/configs/exports.configs';
import { ILoggableData } from './posthog.service';

@Injectable({
  providedIn: 'root',
})
export class FilesService {
  private _BACKEND_URL = environment.API_URL;

  public emitDownloadedFileEvent = new Subject<ILoggableData>();

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _notificationService: NotificationService,
  ) {}

  downloadFile(
    endpoint: string,
    fileType: FileType,
    forcedFileName?: string,
    loggableData?: ILoggableData,
  ): Subject<boolean> {
    let eventId;
    this._notificationService.showNotifForEvent(eventId, {
      message: 'STATUS.CREATION_IN_PROGRESS',
      translateValue: {},
      title: null,
      disableTimeout: true,
      additionalMessage: 'STATUS.CREATION_IN_PROGRESS_DETAILED',
    });

    const listener = new Subject<boolean>();

    this._httpClient
      .get(`${this._BACKEND_URL}/${endpoint}`, {
        responseType: 'blob',
        observe: 'response',
      })
      .pipe(
        map((res) => {
          const contentDisposition = res.headers.get('content-disposition');
          let fileName = forcedFileName ?? `file.${fileType}`;

          if (contentDisposition && !forcedFileName) {
            fileName =
              this._getFileNameFromContentDisposition(contentDisposition);
          }

          return {
            blob: new Blob([res.body], {
              type: MIMETYPES[fileType],
            }),
            fileName,
          };
        }),
      )
      .subscribe(
        (downloadableFile) => {
          this._notificationService.hideNotifForEvent(eventId);
          this._notificationService.showDownload(
            eventId,
            downloadableFile.blob,
            downloadableFile.fileName,
          );
          if (loggableData) {
            this.emitDownloadedFileEvent.next(loggableData);
          }
          listener.next(true);
        },
        (_err) => {
          this._notificationService.hideNotifForEvent(eventId);
          this._notificationService.showError('ALERTS.E_FILE_ERROR');
          listener.next(false);
        },
      );

    return listener;
  }

  private _getFileNameFromContentDisposition(
    contentDisposition: string,
  ): string {
    let fileName = contentDisposition
      .split(';')
      ?.find((part) => part.trim().startsWith('filename='))
      ?.split('=')[1]
      ?.trim();

    if (fileName?.startsWith('"') && fileName?.endsWith('"')) {
      fileName = fileName.slice(1, -1);
    }
    return fileName;
  }
}
